import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Plus, Users, Mail, X, Copy, Check, Link2 } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useModal } from './components/CustomModal';
import axios from 'axios';

import API from './apiConfig';

export default function TeamPage({ onBack }) {
  const { user } = useAuth();
  const modal = useModal();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteUrl, setInviteUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);

  const isOwner = user?.company_role === 'owner' || user?.role === 'admin';

  const fetchCompany = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/company/info`);
      setCompany(data.company);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCompany(); }, [fetchCompany]);

  const generateInvite = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/company/invite`, {
        origin_url: window.location.origin,
      });
      setInviteUrl(data.invite_url);
    } catch (e) {
      modal.alert(e.response?.data?.detail || 'Fehler beim Erstellen des Links', { title: 'Fehler', type: 'warning' });
    }
    setGenerating(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-900/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={onBack} data-testid="team-back-btn" className="p-2 hover:bg-zinc-800 rounded-md transition-colors">
            <ArrowLeft className="w-4 h-4 text-zinc-400" />
          </button>
          <div>
            <h1 className="font-mono text-lg font-medium">Team verwalten</h1>
            <p className="font-mono text-xs text-zinc-500">{company?.name || user?.company || 'Firma'}</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {loading ? (
          <div className="text-center py-12 text-zinc-500 font-mono text-sm">Laden...</div>
        ) : (
          <>
            {/* Owner Info */}
            <div className="p-5 bg-zinc-900 border border-zinc-800 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-mono font-bold text-sm">
                    {(user?.first_name?.[0] || '') + (user?.last_name?.[0] || '')}
                  </div>
                  <div>
                    <p className="font-mono text-sm text-white font-medium">{user?.first_name} {user?.last_name}</p>
                    <p className="font-mono text-xs text-zinc-500">{user?.email}</p>
                  </div>
                </div>
                <span className="px-2.5 py-1 bg-blue-500/10 border border-blue-500/30 rounded-full font-mono text-[10px] text-blue-300">
                  {isOwner ? 'Inhaber' : 'Mitarbeiter'}
                </span>
              </div>
            </div>

            {/* Invite Link (Owner only) */}
            {isOwner && (
              <div className="p-5 bg-zinc-900 border border-zinc-800 rounded-xl space-y-3">
                <div className="flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-blue-400" />
                  <span className="font-mono text-sm text-zinc-300 font-medium">Einladungslink</span>
                </div>
                <p className="text-xs text-zinc-500 font-mono">
                  Teilen Sie diesen Link mit Mitarbeitern. Die Firma wird automatisch vorbelegt. Jeder eingeladene Nutzer zahlt den reduzierten Team-Preis.
                </p>
                {inviteUrl ? (
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={inviteUrl}
                      className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-xs font-mono text-zinc-300 truncate"
                      data-testid="invite-url"
                    />
                    <button onClick={copyLink} data-testid="copy-invite-btn"
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white text-xs font-mono flex items-center gap-1.5">
                      {copied ? <><Check className="w-3.5 h-3.5" /> Kopiert</> : <><Copy className="w-3.5 h-3.5" /> Kopieren</>}
                    </button>
                  </div>
                ) : (
                  <button onClick={generateInvite} disabled={generating} data-testid="generate-invite-btn"
                    className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-mono rounded-lg flex items-center gap-2">
                    <Plus className="w-3.5 h-3.5" /> {generating ? 'Wird erstellt...' : 'Einladungslink erstellen'}
                  </button>
                )}
              </div>
            )}

            {/* Team Members */}
            <div>
              <h2 className="font-mono text-sm font-medium text-zinc-300 mb-4">
                Mitarbeiter ({(company?.members || []).length})
              </h2>
              {(company?.members || []).length > 0 ? (
                <div className="space-y-2">
                  {company.members.map((m, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-emerald-600/20 flex items-center justify-center">
                          <Users className="w-4 h-4 text-emerald-400" />
                        </div>
                        <div>
                          <p className="font-mono text-sm text-white">{m.first_name} {m.last_name}</p>
                          <p className="font-mono text-[10px] text-zinc-500">{m.email}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full font-mono text-[10px] ${
                        m.company_role === 'owner'
                          ? 'bg-blue-500/10 text-blue-400 border border-blue-500/30'
                          : m.subscription_status === 'active'
                            ? 'bg-green-500/10 text-green-400 border border-green-500/30'
                            : 'bg-zinc-800 text-zinc-500 border border-zinc-700'
                      }`}>
                        {m.company_role === 'owner' ? 'Inhaber' : m.subscription_status === 'active' ? 'Aktiv' : 'Ausstehend'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 border border-dashed border-zinc-800 rounded-xl">
                  <Users className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
                  <p className="font-mono text-sm text-zinc-500">Noch keine Mitarbeiter.</p>
                  {isOwner && (
                    <p className="font-mono text-[10px] text-zinc-600 mt-1">
                      Erstellen Sie einen Einladungslink oben.
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
